import { BranchConfigStatus, type Branch } from "~/entities/branch/types";

/**
 * 7차 aw: 데이터 기반 effective status 추론 — BE 의 stored configStatus 와 cameraCount/lineCount/zoneCount 를 함께 보고 UI 표시용 status 결정.
 *
 * transition 기준 (PO):
 * - 0 (대기 중) → 1 (카메라 매핑 진행 중): Camera 1개 이상
 * - 1 → 2 (카메라 매핑 완료): '저장 후 분석 영역 설정' (명시적 액션, BE PATCH 로 stored=2)
 * - 2 → 3 (분석 초안 설정 진행 중): Line/Zone 1개 이상
 * - 3 → 4 (분석 초안 설정 완료): '분석 초안 제출' (명시적 액션, BE PATCH 로 stored=4)
 *
 * stored 값과 데이터 추론 결합: 명시적 액션(2, 4)은 stored 기준, 데이터 추론(1, 3)은 stored 보조.
 */
export function getEffectiveStatus(branch: Pick<Branch, "configStatus" | "cameraCount" | "lineCount" | "zoneCount">): BranchConfigStatus {
  const stored = branch.configStatus;
  const hasCamera = branch.cameraCount > 0;
  const hasLineOrZone = branch.lineCount > 0 || branch.zoneCount > 0;

  // 3, 4: 명시적 액션으로 set 된 stored — monotonic 그대로 유지
  if (stored >= BranchConfigStatus.AnalysisDraft) return stored;
  // 2 (카매핑 완료) + line/zone 있으면 → 3 추론
  if (hasLineOrZone && stored >= BranchConfigStatus.CameraMapped) return BranchConfigStatus.AnalysisDraft;
  // 2: stored 그대로
  if (stored === BranchConfigStatus.CameraMapped) return BranchConfigStatus.CameraMapped;
  // 1: stored 그대로 또는 cam 추론
  if (stored === BranchConfigStatus.CameraMapping) return BranchConfigStatus.CameraMapping;
  if (hasCamera) return BranchConfigStatus.CameraMapping;
  return BranchConfigStatus.Waiting;
}

export function getWorkflowPath(status: BranchConfigStatus): string {
  switch (status) {
    case BranchConfigStatus.Waiting:
      return "resources";
    case BranchConfigStatus.CameraMapping:
    case BranchConfigStatus.CameraMapped:
      return "camera-mapping";
    case BranchConfigStatus.AnalysisDraft:
    case BranchConfigStatus.AnalysisDrafted:
      return "analysis-draft";
  }
}

export function getConfigStatusLabelKey(status: BranchConfigStatus): string {
  switch (status) {
    case BranchConfigStatus.Waiting:
      return "configStatus.waiting";
    case BranchConfigStatus.CameraMapping:
      return "configStatus.cameraMapping";
    case BranchConfigStatus.CameraMapped:
      return "configStatus.cameraMapped";
    case BranchConfigStatus.AnalysisDraft:
      return "configStatus.analysisDraft";
    case BranchConfigStatus.AnalysisDrafted:
      return "configStatus.analysisDrafted";
  }
}

export type StatusVariant = "warning" | "default" | "success";

export function getConfigStatusVariant(status: BranchConfigStatus): StatusVariant {
  switch (status) {
    case BranchConfigStatus.Waiting:
      return "default";
    case BranchConfigStatus.CameraMapping:
    case BranchConfigStatus.AnalysisDraft:
      return "warning";
    case BranchConfigStatus.CameraMapped:
    case BranchConfigStatus.AnalysisDrafted:
      return "success";
  }
}
